import React from 'react'
import { massCheck } from '../timesheetUtils'

const normalizeId = (value) => String(value ?? '').trim()

const UnbilledTab = ({familiesNotBilled = [], otherUnbilledFamilies = [], openStudentModal, teachers = [], month, year, currentCalendarMonth, currentCalendarYear, loadError, loading = false}) => {
    const teacherNamesById = React.useMemo(() => teachers.reduce((teacherMap, teacher) => {
        const teacherId = normalizeId(teacher?.TeacherID)
        if (teacherId) teacherMap[teacherId] = teacher?.Name || 'Unknown teacher'
        return teacherMap
    }, {}), [teachers])

    const generateCleanListOfTeachers = (students = []) => Array.from(new Set(
        students
            .map((student) => teacherNamesById[normalizeId(student?.teachersId)])
            .filter(Boolean)
    )).sort((left, right) => left.localeCompare(right))

    const openFamily = (family) => {
        const familyId = family?.familyId || family?._id
        if (familyId && typeof openStudentModal === 'function') openStudentModal(familyId)
    }

    const displayedPeriod = [month, year].filter(Boolean).join(' ')
    const displayedCalendarPeriod = [currentCalendarMonth, currentCalendarYear].filter(Boolean).join(' ')
    const countStudents = (families) => families.reduce(
        (total, family) => total + (Array.isArray(family?.students) ? family.students.length : 0),
        0
    )

    const renderFamilyRows = (families, sectionName) => families.map((family) => {
        const teacherNames = generateCleanListOfTeachers(family.students)

        return (
            <tr key={`${sectionName}-${family.familyId || family._id || family.familyName}`} className="each-row">
                <td><input className="student-checkbox" type="checkbox" value={family.primaryEmail || family.email || ''} aria-label={`Select ${family.familyName || 'family'}`}/></td>
                <td onClick={() => openFamily(family)}>{family.familyName || 'Unnamed family'}</td>
                <td onClick={() => openFamily(family)}>{family.primaryEmail || family.email || 'No email'}</td>
                <td onClick={() => openFamily(family)}>
                    <div className="unbilled-families__students">
                        {family.students.map((student) => (
                            <span className="unbilled-families__student" key={student.studentId || student._id || student.studentName}>
                                {student.studentName || 'Unnamed student'}
                            </span>
                        ))}
                    </div>
                </td>
                <td onClick={() => openFamily(family)}>{family.school || '—'}</td>
                <td onClick={() => openFamily(family)}>
                    {teacherNames.map((teacher) => <p key={teacher}>{teacher}</p>)}
                    {teacherNames.length === 0 && <span className="unbilled-families__muted">Unassigned</span>}
                </td>
            </tr>
        )
    })

    return(
        <div className="unbilled-families">
        <div className="unbilled-families__summary">
            <h3>Active student billing review for {displayedPeriod || 'the selected month'}</h3>
            <p>The first section contains students needing attention now. The second contains the remaining students who were missing from {displayedPeriod || 'the selected month'} timesheets but are already scheduled in {displayedCalendarPeriod || 'the current month'}.</p>
        </div>
        {loadError && <div className="unbilled-families__error" role="alert">{loadError}</div>}
        <table className="student-table unbilled-families__table">
            <thead>
                <tr>
                    <th><input onChange={(e) => massCheck(e.target.checked)} type="checkbox" aria-label="Select all unbilled families" /></th>
                    <th>Family Name</th>
                    <th>Email</th>
                    <th>Missing Active Students</th>
                    <th>School</th>
                    <th>Teachers</th>
                </tr>
            </thead>
            {loading && <tbody>
                <tr>
                    <td className="unbilled-families__loading" colSpan="6">
                        <span />
                        <span />
                        <span />
                    </td>
                </tr>
            </tbody>}
            {!loading && !loadError && <tbody className="unbilled-families__section">
                <tr className="unbilled-families__section-row">
                    <td colSpan="6">
                        <div className="unbilled-families__section-title">
                            <span>Needs attention now</span>
                            <strong>{countStudents(familiesNotBilled)} students</strong>
                        </div>
                        <p>Missing from {displayedPeriod || 'the selected month'} timesheets and not scheduled in {displayedCalendarPeriod || 'the current month'}.</p>
                    </td>
                </tr>
                {renderFamilyRows(familiesNotBilled, 'needs-attention')}
                {familiesNotBilled.length === 0 && (
                    <tr>
                        <td className="unbilled-families__empty" colSpan="6">No active students currently need immediate review.</td>
                    </tr>
                )}
            </tbody>}
            {!loading && !loadError && <tbody className="unbilled-families__section unbilled-families__section--complete">
                <tr className="unbilled-families__section-row">
                    <td colSpan="6">
                        <div className="unbilled-families__section-title">
                            <span>Missing from timesheets but scheduled this month</span>
                            <strong>{countStudents(otherUnbilledFamilies)} students</strong>
                        </div>
                        <p>These students are omitted from the section above because they already have a calendar lesson this month.</p>
                    </td>
                </tr>
                {renderFamilyRows(otherUnbilledFamilies, 'scheduled-list')}
                {otherUnbilledFamilies.length === 0 && (
                    <tr>
                        <td className="unbilled-families__empty" colSpan="6">No additional students are missing from {displayedPeriod || 'the selected month'} timesheets.</td>
                    </tr>
                )}
            </tbody>}
        </table>
    </div>
    )
}
export default UnbilledTab
